/* eslint-disable max-len */
import _ from 'underscore';
import Promise from 'bluebird';

import {
  RecommendationIDPriorityMapping,
  RecommendationStatus,
} from '../constants';
import { getErrorString } from './utils';

export const getImportRecommendationService = ({
  api, storage, settings, logger,
}) => {
  const patchRecommendationStatus = (recommendation, status) => {
    //
    if (!recommendation || !status) {
      const recId = recommendation ? recommendation.id : null;
      logger.logError(`[ImportCalloutCard] Unexpected recommendation or status, RID:${recId}, status:${status}`);
      return Promise.resolve(recommendation);
    }

    logger.logTrace(`[ImportCalloutCard] updating status for recommendation, RID:${recommendation.id}, status:${status}`);

    return api.patch({ id: recommendation.id, status }).then((value) => {
      if (value) {
        // API call succeeded, time-stamp it
        if (value.Status === RecommendationStatus.Adopted) {
          logger.logTrace(`[ImportCalloutCard] recommendation is adopted, RID:${recommendation.id}`);
          storage.updateLastAdoptionTime(recommendation.id);
        }

        logger.logTrace(`[ImportCalloutCard] updated status for recommendation, RID:${recommendation.id}, status:${value.Status}`);

        return {
          ...recommendation,
          status: value.Status,
          dismissCount: value.DismissCount,
          dismissalExpirationDateTime: value.DismissalExpirationDateTime,
          clientName: value.ClientName,
          descriptionHint: value.DescriptionHint,
        };
      }
      return null;
    }).catch((err) => {
      // save unsent actions, and will retry to sent later
      logger.logTrace(`[ImportCalloutCard] save failed action, error:${getErrorString(err)}, RID:${recommendation.id}, status:${status}`);
      storage.addUnsentAction({ recommendationId: recommendation.id, status });
    });
  };

  const adoptRecommendation = recommendation => patchRecommendationStatus(recommendation, RecommendationStatus.Adopted);

  const dismissRecommendation = recommendation => patchRecommendationStatus(recommendation, RecommendationStatus.Dismissed);

  const clickRecommendation = recommendation => patchRecommendationStatus(recommendation, RecommendationStatus.Clicked);

  const viewRecommendation = recommendation => patchRecommendationStatus(recommendation, RecommendationStatus.Viewed);

  const retryUnsentActions = () => {
    const { unsentActions } = storage;
    if (unsentActions && unsentActions.length > 0) {
      logger.logTrace(`[ImportCalloutCard] retrying sending actions, count:${unsentActions.length}`);
      return Promise.each(unsentActions, (unsentAction, index) => {
        const {
          recommendationId: id, status, timestamp, timestampStr,
        } = unsentAction;

        logger.logTrace(`[ImportCalloutCard] retrying action #${index}/${unsentActions.length}, RID:${id}, status:${status}, ts:${timestampStr}`);

        if ((Date.now() - new Date(timestamp)) < settings.UnsentActionCacheExpirationTimeInMilliSecs) {
          return api.patch({ id, status }).then((value) => {
            logger.logTrace(`[ImportCalloutCard] successfully resent action, RID:${id}, status:${status}, ts:${timestampStr}`);
            // API call succeeded, time-stamp it
            if (value && value.Status === RecommendationStatus.Adopted) {
              // update last adoption time, using the original timestamp
              storage.updateLastAdoptionTime(id, timestamp, timestampStr);
            }
            // remove action from storage
            storage.removeUnsentAction(unsentAction);
          }).tapCatch((err) => {
            logger.logError(`[ImportCalloutCard] Failed to retry action, error:${getErrorString(err)}, RID:${id}, status:${status}`);
          });
        }

        // remove expired action
        logger.logTrace(`[ImportCalloutCard] unsent action is expired, RID:${id}, status:${status}, ts:${timestampStr}`);
        storage.removeUnsentAction(unsentAction);
        return Promise.resolve(null);
      });
    }
    return Promise.resolve(null);
  };

  const getRecommendations = () => {
    // Get recommendations only after successful post in order to receive up-to-date values
    const result = retryUnsentActions().then(() => {
      logger.logTrace('[ImportCalloutCard] retried unsent actions');

      const { lastAdoptionTime } = storage;
      // Haven't adopted a recommendation or passed the cool-down period
      if (!lastAdoptionTime || !lastAdoptionTime.timestamp ||
            (Date.now() - new Date(lastAdoptionTime.timestamp)) > settings.GoogleImportRecommendationCoolDownPeriodInMilliSecs) {
        //
        // remove last adoption time if it's expired
        if (lastAdoptionTime) {
          storage.removeLastAdoptionTime();
        }

        logger.logTrace('[ImportCalloutCard] start getting recommendations');

        return api.get().then((values) => {
          if (values && values.length > 0) {
            const recommendations = values.map(({ Id, Status, DismissCount }) => {
              const recommendation = {
                id: Id,
                dismissCount: DismissCount,
                status: Status,
                priority: RecommendationIDPriorityMapping[Id],
              };
              return recommendation;
            });

            logger.logTrace(`[ImportCalloutCard] got recommendations:${JSON.stringify(recommendations)}, count:${recommendations.length}`);

            return _.filter(recommendations, r => _.isNumber(r.priority));
          }
          return null;
        }).catch((err) => {
          logger.logError(`[ImportCalloutCard] Failed to get recommendations, error:${getErrorString(err)}`);
          return null;
        });
      }

      logger.logTrace(`[ImportCalloutCard] skip getRecommendations as within cool-down period, RID:${lastAdoptionTime.recommendationId}, ts:${lastAdoptionTime.timestampStr}`);
      return Promise.resolve(null);
    }).catch((err) => {
      // failed to resend unsent actions
      logger.logError(`[ImportCalloutCard] failed to retry unsent actions, error:${getErrorString(err)}`);
      return null;
    });
    return result;
  };

  const getHighestPriorityRecommendation = () =>
    getRecommendations().then((recommendations) => {
      if (recommendations && recommendations.length > 0) {
        const highestPriorityRecommendation = _.min(recommendations, r => r.priority);
        // #TEST# const highestPriorityRecommendation = _.find(recommendations, r => r.id === 200);
        logger.logTrace(`[ImportCalloutCard] got highest priority recommendation, recommendation:${JSON.stringify(highestPriorityRecommendation)}, RID:${highestPriorityRecommendation.id}`);
        return highestPriorityRecommendation;
      }
      return null;
    });

  return {
    getRecommendations,
    getHighestPriorityRecommendation,
    patchRecommendationStatus,
    // todo: retry unsent in UI
    retryUnsentActions,
    viewRecommendation,
    clickRecommendation,
    dismissRecommendation,
    adoptRecommendation,
  };
};
